import { useState, useEffect, useRef } from 'react';
import useIsMobile from '../../utils/hooks/useIsMobile';
import { searchCities } from '../../utils/api/citiesApi';
import { joinCommunity } from '../../utils/api/communityApi';
import s from '../../styles/components/rewards/RewardsSignup.module.css';
import site from '../../data/master.json';

const ROWS = 3;
const PER_HALF = 10;

function buildRows(covers) {
  return Array.from({ length: ROWS }, (_, r) =>
    Array.from({ length: PER_HALF }, (_, i) => covers[(i + r * 2) % covers.length])
  );
}

const ROW_COVERS = buildRows(site.discography.songs.map((song) => song.image));

export default function RewardsSignup() {
  const b = site.rewardsSignup;
  const isMobile = useIsMobile();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [city, setCity] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '' });
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const requestId = useRef(0);

  useEffect(() => {
    if (city || query.trim().length < 2) {
      requestId.current += 1;
      setResults([]);
      setSearching(false);
      return undefined;
    }

    requestId.current += 1;
    const id = requestId.current;
    setSearching(true);

    const timer = setTimeout(async () => {
      try {
        const found = await searchCities(query.trim());
        if (id === requestId.current) {
          setResults(found);
          setSearching(false);
        }
      } catch (err) {
        console.error(err);
        if (id === requestId.current) setSearching(false);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [query, city]);

  const handleQuery = (e) => {
    setQuery(e.target.value);
    setCity(null);
  };

  const pickCity = (picked) => {
    setCity(picked);
    setQuery(picked.name);
    setResults([]);
  };

  const handleCityKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!city && results[0]) pickCity(results[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError(false);
    try {
      await joinCommunity({
        name: form.name,
        phone: form.phone,
        smsConsent: consent,
        city: { name: city.name, coordinates: city.coordinates },
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setError(true);
    }
    setSending(false);
  };

  const showList = !city && query.trim().length >= 2;

  return (
    <div className={s.container}>
      <div className={s.galleryWrap}>
        {ROW_COVERS.map((row, r) => (
          <div key={r} className={s.row}>
            <div className={r % 2 === 1 ? s.trackRight : s.trackLeft}>
              {[...row, ...row].map((src, i) => (
                <div key={i} className={s.cell}>
                  <img src={src} alt="" className={s.cover} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className={s.overlay} />

      <div className={isMobile ? s.formWrapMobile : s.formWrap}>
        {!submitted ? (
          <>
            <span className={s.eyebrow}>{b.eyebrow}</span>
            <h1 className={s.headline}>{b.headline}</h1>
            <p className={s.subhead}>{b.subhead}</p>

            <form className={s.form} onSubmit={handleSubmit}>
              <label className={s.field}>
                <span className={s.label}>{b.cityLabel}</span>
                <input
                  className={s.input}
                  type="text"
                  placeholder={b.cityPlaceholder}
                  value={query}
                  onChange={handleQuery}
                  onKeyDown={handleCityKey}
                  autoComplete="off"
                  required
                />
              </label>

              {showList && (
                <ul className={s.results}>
                  {results.map((c) => (
                    <li key={c.id}>
                      <button type="button" className={s.result} onClick={() => pickCity(c)}>
                        {c.name}
                      </button>
                    </li>
                  ))}
                  {searching && <li className={s.empty}>{b.searching}</li>}
                  {!searching && results.length === 0 && <li className={s.empty}>{b.noResults}</li>}
                </ul>
              )}

              {city && (
                <>
                  <label className={s.field}>
                    <span className={s.label}>{b.nameLabel}</span>
                    <input
                      className={s.input}
                      type="text"
                      placeholder={b.namePlaceholder}
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                    />
                  </label>

                  <label className={s.field}>
                    <span className={s.label}>{b.phoneLabel}</span>
                    <input
                      className={s.input}
                      type="tel"
                      placeholder={b.phonePlaceholder}
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      required
                    />
                  </label>

                  <label className={s.consent}>
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      required
                    />
                    <span>{b.consent}</span>
                  </label>

                  {error && <p className={s.error}>{b.errorMessage}</p>}

                  <button type="submit" className={s.submitBtn} disabled={sending}>
                    {b.submitBtn}
                  </button>
                </>
              )}
            </form>

            <p className={s.fine}>{b.submitFinePrint}</p>
          </>
        ) : (
          <div className={s.successWrap}>
            <span className={s.eyebrow}>{b.successEyebrow}</span>
            <h2 className={s.successHeadline}>{b.successHeadline}</h2>
            <p className={s.subhead}>{b.successSubhead}</p>
            <div className={s.successBtns}>
              {b.successButtons.map((btn) => (
                <a key={btn.url} href={btn.url} className={s.successBtn}>
                  {btn.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}